// src/components/profile/SaveProfileOptIn.tsx
// "Save this profile" opt-in form shown to anonymous visitors after 10 seconds on a profile
'use client';

import { useEffect, useState, useRef } from 'react';
import { X, Bookmark, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface SaveProfileOptInProps {
  profileId: string;
  profileName: string;
  searchTerm?: string;
}

const STORAGE_KEY_PREFIX = 'zamzi-saved-profile-';
const DELAY_MS = 12000; // Show after 12 seconds

export function SaveProfileOptIn({ profileId, profileName, searchTerm }: SaveProfileOptInProps) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Don't show if already saved this profile on this device
    const alreadySaved = localStorage.getItem(`${STORAGE_KEY_PREFIX}${profileId}`);
    if (alreadySaved) return;

    timerRef.current = setTimeout(() => {
      setVisible(true);
    }, DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [profileId]);

  const isEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  const isPhone = (val: string) => /^[0-9+\s\-()]{7,20}$/.test(val.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    const trimmedContact = contact.trim();

    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }
    if (!isEmail(trimmedContact) && !isPhone(trimmedContact)) {
      setError('Please enter a valid email or WhatsApp number.');
      return;
    }

    setIsLoading(true);
    try {
      const payload: Record<string, string> = {
        name: trimmedName,
        profileName,
      };
      if (searchTerm) payload.searchTerm = searchTerm;
      if (isEmail(trimmedContact)) payload.email = trimmedContact;
      else payload.phone = trimmedContact;

      const res = await fetch(`/api/profile/${encodeURIComponent(profileId)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Something went wrong.');
      }

      // Mark saved on this device so we don't prompt again
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${profileId}`, '1');
      setSaved(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  if (!visible || dismissed) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-80 rounded-xl border border-primary/20 bg-white shadow-2xl animate-in slide-in-from-bottom-4"
      role="dialog"
      aria-label="Save this profile"
    >
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-xl bg-primary px-4 py-3">
        <div className="flex items-center gap-2 text-white">
          <Bookmark className="h-4 w-4" />
          <span className="text-sm font-semibold">Interested in {profileName}?</span>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-white/70 hover:text-white transition"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        {saved ? (
          <div className="flex flex-col items-center gap-2 py-2 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <p className="text-sm font-semibold text-foreground">You're saved!</p>
            <p className="text-xs text-muted-foreground">
              {profileName} will be able to follow up with you.
            </p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setDismissed(true)}>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Save your interest — {profileName} can follow up with you directly.
            </p>

            <Input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="text-sm"
              disabled={isLoading}
              autoComplete="name"
            />
            <Input
              placeholder="Email or WhatsApp number"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              maxLength={200}
              className="text-sm"
              disabled={isLoading}
              autoComplete="email"
              type="text"
              inputMode="email"
            />

            {error && (
              <p className="text-xs text-destructive">{error}</p>
            )}

            <Button type="submit" size="sm" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Saving...</>
              ) : (
                <><Bookmark className="mr-2 h-3 w-3" />Save My Interest</>
              )}
            </Button>

            <p className="text-[10px] text-muted-foreground text-center">
              Only {profileName} will see your contact. Not shared with anyone else.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
